/**
 * Mirrors of the C# Logistics › Issuance DTO records, field for field.
 *
 * Its own file rather than more of `stockTypes.ts`, on the reason that file gives for splitting
 * from `inventoryTypes.ts`: a module that would double the length of a file nobody could then
 * see the shape of gets its own. The three are read together and none imports the others except
 * for the shared list query.
 */

import type { ListQuery } from '../../administration/users/userTypes';
import { ITEM_KIND } from './inventoryTypes';
import type { InventoryItemKind } from './inventoryTypes';

/**
 * How stock leaves a store. Enums serialize as int, so the numbers are the contract.
 *
 * <b>3 and 4 are reserved</b> — Sell and Transfer in the source. Sell exists there to post cost
 * of goods sold against a general ledger this product does not have; Transfer was retired by the
 * source itself, which refuses one and points at a stock-transfer record. Holding the numbers is
 * what makes adding either additive.
 */
export const ISSUANCE_TYPE = {
  dispense: 0,
  assign: 1,
  lend: 2,
} as const;

export type IssuanceType = (typeof ISSUANCE_TYPE)[keyof typeof ISSUANCE_TYPE];

/**
 * ONE label map, and it must agree with the server's `IssuanceLabels`.
 *
 * Two copies of one vocabulary is how a module shows a reader two words for one enum — this
 * suite already paid for that when two services each carried a private kind label and a form
 * offered "Asset" under a tab reading "Equipment". The copy across the wire is unavoidable; a
 * second copy on this side is not.
 */
export const ISSUANCE_TYPE_LABEL: Record<IssuanceType, string> = {
  [ISSUANCE_TYPE.dispense]: 'Dispense',
  [ISSUANCE_TYPE.assign]: 'Assign',
  [ISSUANCE_TYPE.lend]: 'Lend',
};

/**
 * WHICH METHODS MAY RELEASE A KIND — the frontend half of
 * <c>IssuanceMethods.PermitsKind</c>, and it must agree with it.
 *
 * <b>A second copy of one rule is a rule that drifts</b>, which this suite has already paid
 * for twice. It could not be shared: the Domain is C# and the picker has to narrow itself
 * before any request is made, so the alternative is a round trip to learn which options to
 * draw.
 *
 * <b>NOTHING PINS THE TWO, and that is a stated gap rather than a decision.</b> No test here
 * can read a C# file and no test there can read this one, so the agreement rests on whoever
 * edits one remembering the other. What makes the drift survivable rather than silent is that
 * the SERVER still refuses — a method this list wrongly offers produces a rejection rather
 * than a bad write. A widening here is the dangerous direction; a narrowing only hides a
 * method that would have worked.
 *
 * <b>A NULL kind is a form opened cold</b> — the toolbar shortcut, with nothing chosen — and
 * gets every method, because the item has not been decided yet and the FEED is what narrows
 * it. A kind that is named narrows the method instead, because there the item IS decided.
 */
export function methodsForKind(kind: InventoryItemKind | null | undefined): IssuanceType[] {
  const all = Object.values(ISSUANCE_TYPE);

  if (kind === null || kind === undefined) return all;

  return all.filter((type) =>
    kind === ITEM_KIND.consumable
      ? type === ISSUANCE_TYPE.dispense
      : kind === ITEM_KIND.asset || kind === ITEM_KIND.subscription
        ? type === ISSUANCE_TYPE.assign || type === ISSUANCE_TYPE.lend
        : false,
  );
}

/** What each method is FOR, shown as the picker's sublabel so the choice explains itself. */
export const ISSUANCE_TYPE_HINT: Record<IssuanceType, string> = {
  [ISSUANCE_TYPE.dispense]: 'An expendable handed over to be used up. Nothing comes back.',
  [ISSUANCE_TYPE.assign]: "Into a staff member's custody, against an ICS or a PAR. Returnable.",
  [ISSUANCE_TYPE.lend]: 'Out to an external party against a due date. Returnable.',
};

/**
 * Where an issuance stands.
 *
 * <b>0 and 5 are reserved</b> — Draft and Cancelled. Stock moves when the issuance is written,
 * so there is no stage at which a slip exists and the stock has not left.
 */
export const ISSUANCE_STATUS = {
  released: 1,
  partiallyReturned: 2,
  returned: 3,
  completed: 4,
} as const;

export type IssuanceStatus = (typeof ISSUANCE_STATUS)[keyof typeof ISSUANCE_STATUS];

/**
 * The reader's words, not the enum's.
 *
 * `Released` reads as **Out** and `Completed` as **Issued**: with no draft stage there is no
 * release to distinguish from a creation, and what a reader wants to know is whether anything
 * is still out.
 */
export const ISSUANCE_STATUS_LABEL: Record<IssuanceStatus, string> = {
  [ISSUANCE_STATUS.released]: 'Out',
  [ISSUANCE_STATUS.partiallyReturned]: 'Partly returned',
  [ISSUANCE_STATUS.returned]: 'Returned',
  [ISSUANCE_STATUS.completed]: 'Issued',
};

/** Which property-accountability form a custody issuance was signed against. */
export const PROPERTY_CLASSIFICATION = {
  ics: 0,
  par: 1,
} as const;

export type PropertyClassification =
  (typeof PROPERTY_CLASSIFICATION)[keyof typeof PROPERTY_CLASSIFICATION];

/**
 * The government forms' own names, so this is not invented terminology.
 *
 * An Inventory Custody Slip covers property at or below the threshold and a Property
 * Acknowledgement Receipt covers the rest.
 *
 * <b>The class is STAMPED ON THE STOCK when it is received, and the reader CHOOSES which form
 * a slip is.</b> The two are different documents with different numbers, so one slip carries
 * one form and may only draw stock recorded under it — which is why the picker asks before a
 * single line is chosen. It used to be derived from value on every read and stored nowhere.
 */
export const PROPERTY_CLASSIFICATION_LABEL: Record<PropertyClassification, string> = {
  [PROPERTY_CLASSIFICATION.ics]: 'ICS',
  [PROPERTY_CLASSIFICATION.par]: 'PAR',
};

/**
 * What each form is FOR, shown as the picker’s sublabel so the choice explains itself.
 *
 * The words are the government forms’ own, which is what keeps this out of invented
 * terminology — and the second half of each states the consequence the reader needs before
 * choosing: only stock recorded under it can go on the slip.
 */
export const PROPERTY_CLASSIFICATION_HINT: Record<PropertyClassification, string> = {
  [PROPERTY_CLASSIFICATION.ics]:
    'Inventory Custody Slip — lower-value property. Only ICS-recorded stock can be issued.',
  [PROPERTY_CLASSIFICATION.par]:
    'Property Acknowledgement Receipt — higher-value property. Only PAR-recorded stock can be issued.',
};

/** Whether the method expects the stock back. Mirrors `IssuanceMethods.IsCustody`. */
export function isCustody(type: IssuanceType): boolean {
  return type === ISSUANCE_TYPE.assign || type === ISSUANCE_TYPE.lend;
}

export interface IssuanceListItem {
  id: string;
  /** `ISS-{yy}-{NNNNN}` — allocated by the server, permanent, absent from the request. */
  issuanceNumber: string;
  /** The ICS/PAR number on the signed form. Null for a dispense. */
  controlNumber: string | null;
  issuanceType: IssuanceType;
  /** Resolved server-side, so two surfaces cannot disagree about the word. */
  issuanceTypeLabel: string;
  status: IssuanceStatus;
  acknowledgementType: PropertyClassification | null;
  /** Resolved through the identity store by the caller. Never a name on this row. */
  accountablePersonnelId: string | null;
  receivingOrgUnitId: string | null;
  receivingOrgUnitName: string | null;
  /** An organization's name for a lend. Never a person's. */
  externalPartyName: string | null;
  expectedReturnDate: string | null;
  /** Derived on every read. The row marker and the rail's count read one rule. */
  isOverdue: boolean;
  lineCount: number;
  totalQuantity: number;
  outstandingQuantity: number;
  /** Null where nothing on the slip was priced — an unpriced release is not a free one. */
  totalValue: number | null;
  referenceNumber: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * One article issued to an employee — a line of a slip naming them as accountable officer, or as
 * recipient where the slip names no officer.
 * What the personnel profile's Issued Assets panel lists. Mirrors `IssuedAssetLine`.
 *
 * Every nullable field may arrive `undefined`: the server omits a null property entirely, so a
 * comparison against one is `== null`, never `=== null`.
 */
export interface IssuedAssetLine {
  /** The issuance LINE — what a return is recorded against. */
  id: string;
  issuanceId: string;
  issuanceNumber: string;
  issuanceType: IssuanceType;
  issuanceTypeLabel: string;
  status: IssuanceStatus;
  acknowledgementType: PropertyClassification | null;
  expectedReturnDate: string | null;
  isOverdue: boolean;
  sku: string;
  /** The governed name — the item TYPE's. */
  itemName: string;
  manufacturer: string | null;
  brandName: string | null;
  model: string | null;
  unitOfMeasureCode: string | null;
  serialNumber: string | null;
  inventoryNumber: string | null;
  quantity: number;
  quantityReturned: number;
  /** The SLIP's live/deleted state. */
  isActive: boolean;
  /** When the slip was written — the day the article was issued. */
  createdAt: string;
  createdByName: string | null;
  /** The LINE's last change — the most recent return recorded against it. */
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface IssuanceAllocationLine {
  stockBatchId: string;
  batchNumber: string;
  expiryDate: string | null;
  quantity: number;
}

export interface IssuanceItemLine {
  id: string;
  inventoryItemId: string;

  /*
    WHICH GROUP this belongs to on Stock Levels — the item's TYPE, not the item.

    That table groups by item type and fetches one group's figures at a time, so a save has to
    be able to name the groups it moved or the reload cannot be scoped to them. A slip carries
    several lines across several types, which is why it rides on the LINE.
  */
  inventoryItemTypeId: string;
  sku: string;
  itemName: string;

  /*
    THE PRODUCT — manufacturer, brand and model, joined SERVER-side (`InventoryItemIdentity`),
    the purchase order line's call. `itemName` is the TYPE's, so it is what this falls back to.
  */
  itemIdentity: string;
  unitOfMeasureCode: string | null;
  storageFacilityId: string;
  storageFacilityName: string;
  quantity: number;
  quantityReturned: number;
  unitCostSnapshot: number | null;

  /*
    NO CLASS ON THE LINE. The slip names one form and every line on it is recorded under that
    form, so a per-line value would be a second answer to a question the header settles. A
    mixed slip is no longer expressible.
  */
  stockUnitId: string | null;
  serialNumber: string | null;

  /*
    THE ICS / PAR PRINT COLUMNS, and every one is a JOIN on the server.

    Three off the serialized UNIT, so a quantity line reads null for all three; three off the
    CATALOG ROW, so every line has them. None is stored on the line, and nothing here writes
    any of them.
  */
  inventoryNumber: string | null;
  fund: string | null;
  acquisitionDate: string | null;
  description: string | null;
  estimatedUsefulLifeYears: number | null;
  tracksExpiry: boolean;

  allocations: IssuanceAllocationLine[];
}

export interface IssuanceReturnLine {
  id: string;

  /** The line it came back against — the profile draws the return on that line's row. */
  issuanceItemId: string;
  quantity: number;
  returnedOn: string;
  storageFacilityId: string;
  storageFacilityName: string;
  condition: string | null;
  notes: string | null;
  createdAt: string;
  createdByName: string | null;
}

/**
 * The whole issuance.
 *
 * FLAT rather than extending the row, mirroring the server: every DTO in this product is a
 * standalone shape, and the two are free to diverge — a row states how much is outstanding, a
 * page states which lots it came from.
 */
export interface IssuanceDetail extends Omit<IssuanceListItem, never> {
  receivedByPersonnelId: string | null;

  /** The post that signatory held when they signed. A snapshot, derived server-side. */
  receivedByPosition: string | null;

  issuedByPersonnelId: string | null;

  /** The post the releasing signatory held. A snapshot, derived server-side. */
  issuedByPosition: string | null;

  externalPartyContact: string | null;

  /**
   * The individual at the external party, as an id.
   *
   * NO NAME, here or anywhere else on the wire. The page resolves it the way it resolves the
   * three signatories — one batch call for the record on screen.
   */
  externalPartyContactPersonId: string | null;
  externalPartyProvince: string | null;
  externalPartyCityMunicipality: string | null;
  externalPartyBarangay: string | null;
  externalPartyStreetAddress: string | null;

  /**
   * The LGU or office printed at the head of the ICS / PAR form.
   *
   * <b>DERIVED from the accountable officer own unit</b> from 06 Oct 2026, and snapshotted
   * at release — so moving somebody office does not rewrite the slip they signed. The
   * CREATE request has no field for it; this is the read side, which is what the profile
   * states.
   */
  lguName: string | null;

  /** The fund cluster printed at the head of the ICS / PAR form. */
  fundCluster: string | null;

  purpose: string | null;
  notes: string | null;
  items: IssuanceItemLine[];
  returns: IssuanceReturnLine[];
}

/**
 * One item issuable right now, under the chosen method.
 *
 * Stock that cannot be issued never appears — an item of the wrong kind, one with nothing
 * available, and a date-lapsed lot are all absent, so the picker cannot produce a rejection.
 */
export interface IssuableItem {
  id: string;

  /*
    WHICH GROUP this belongs to on Stock Levels — the item's TYPE, not the item.

    That table groups by item type and fetches one group's figures at a time, so a save has to
    be able to name the groups it moved or the reload cannot be scoped to them. A slip carries
    several lines across several types, which is why it rides on the LINE.
  */
  inventoryItemTypeId: string;
  sku: string;
  name: string;

  /*
    WHAT TELLS TWO ITEMS OF ONE GOVERNED NAME APART — the same three `IssuableUnit` below
    already carries, and the same three the Stock Levels anchor joins. `name` is the item
    TYPE's, so without these a catalog holding three laptops offers "Laptop" three times.

    All three are optional, so `lib/itemIdentity` falls back to the name: a blank label is an
    option nobody can choose.
  */
  manufacturer?: string | null;
  brandName?: string | null;
  model?: string | null;
  unitOfMeasureCode: string | null;
  kind: InventoryItemKind;
  tracksSerial: boolean;
  available: number;
}

export interface IssuableSource {
  storageFacilityId: string;
  storageFacilityName: string;
  available: number;
}

export interface IssuableUnit {
  id: string;
  serialNumber: string;

  /*
    WHICH ITEM THIS IS ONE OF — the picker joins this id back to the ITEM for the identity
    line it labels the option with.

    THE THREE IDENTITY FIELDS ARE NOT HERE, and that is a removal rather than an omission:
    they were on the wire with no reader from 29 Sep 2026 until 06 Oct 2026, when the picker
    needed an identity and read it off `IssuableItem` — which the same loop already holds.
    One source, so the two halves of one item’s options cannot disagree.
  */
  inventoryItemId: string;
  itemName: string;
  sku: string;
  storageFacilityId: string;
  storageFacilityName: string;
  unitCost: number | null;

  /** Which form this unit is RECORDED under. Null where it was never priced. */
  classification: PropertyClassification | null;
}

/**
 * One line being issued.
 *
 * A serialized line names the UNIT and nothing else — its store is the unit's own, so a facility
 * sent alongside would be a second answer to a question the unit already settles.
 */
export interface CreateIssuanceLine {
  inventoryItemId: string;
  storageFacilityId: string | null;
  quantity: number;
  stockUnitId: string | null;
}

/**
 * Release stock. There is no draft, so this is the act itself.
 *
 * <b>There is no number, status or acknowledgement on it.</b> All three are the server's — the
 * number is allocated there and the rest follow from the method, which is what keeps a caller
 * from filing a release as already returned.
 */
export interface CreateIssuanceRequest {
  issuanceType: IssuanceType;

  /**
   * Which form is being signed. REQUIRED on a custody method, REFUSED on every other.
   *
   * <b>It scopes the stock rather than describing it</b> — only lots and units recorded under
   * this class may go on the slip, which is why the form asks before a line is chosen.
   */
  acknowledgementType: PropertyClassification | null;
  accountablePersonnelId: string | null;
  receivingOrgUnitId: string | null;
  externalPartyName: string | null;
  externalPartyContact: string | null;

  /*
    THE CONTACT PERSON IS SENT AS A NAME AND STORED AS AN ID.

    The server writes it to the identity store first and keeps only the `PersonId` it comes
    back as — so nothing on the read side ever carries these four, and the detail above has no
    field for them. A caller supplying a name they already know is not a reveal, which is what
    keeps this on `issuances.create`.
  */
  externalPartyContactFirstName: string | null;
  externalPartyContactMiddleName: string | null;
  externalPartyContactLastName: string | null;
  externalPartyContactSuffix: string | null;

  externalPartyProvince: string | null;
  externalPartyCityMunicipality: string | null;
  externalPartyBarangay: string | null;
  externalPartyStreetAddress: string | null;

  expectedReturnDate: string | null;
  receivedByPersonnelId: string | null;
  issuedByPersonnelId: string | null;

  /*
    THE POSTS ARE NOT SENT, and their absence is the point.

    A snapshot on a signed government form that a caller could name is one a caller could
    misname, so the server derives both off the substantive line of service — the same
    derivation the personnel list reports. The source puts them on its own request; this does
    not, which is the `recordNumber` shape: unspoofable because there is nothing to spoof.
  */

  /*
    THERE IS NO LGU FIELD, and the absence is the point — product call, 06 Oct 2026, in the
    reader own words: *remove the LGU/Office, instead auto detect the unit of the accountable
    person*.

    The server derives it from the accountable officer own org unit, off the substantive line
    of service — the same derivation the two position snapshots above already take, and for
    the same reason: a permanent snapshot at the head of a signed government form that a
    caller could name is one a caller could misname.
  */

  /**
   * The fund cluster printed at the head of the ICS / PAR form. Custody only.
   *
   * <b>The FORM offers the three PPA expense classes</b> from 06 Oct 2026 — the column stays
   * free text and the value is the label, because nothing branches on this or aggregates by
   * it and two slips recorded before the change carry genuine fund clusters a closed set
   * would have made unwritable. The picker is what constrains a reader.
   */
  fundCluster: string | null;

  referenceNumber: string | null;
  purpose: string | null;
  notes: string | null;
  items: CreateIssuanceLine[];
}

export interface RecordIssuanceReturnRequest {
  issuanceItemId: string;
  quantity: number;
  returnedOn: string;
  storageFacilityId: string | null;
  condition: string | null;
  notes: string | null;
}

export interface IssuanceListQuery extends ListQuery {
  type?: string;
  stage?: string;
  facility?: string;
  /** Its own axis, not a value on `stage` — an issuance is Out AND overdue. */
  attention?: string;
}
